import { ConditionGroup, FlowNode, InsertionTarget, TriggerInstance } from "./types";
import { BRANCHING_TYPES, findActionDef, getDefaultNodeConfig, ON_FAILURE_BRANCH_ID } from "./catalog";
import { buildDefaultEmailHtml } from "./email-action-editor";

// Matches builder-page.tsx's own SEND_NODE_TYPES / flow-canvas.tsx's own
// SEND_TYPES (each file keeps its own copy rather than a shared export) —
// used here only to tell serializeFlow's walk when a node's `.branches` are
// the new Send-node "always"/"fallback" pair (pending backend deploy) rather
// than a real Condition/Branch/Holdout/AI-Moderate true/false check.
const SEND_TYPES_FOR_SERIALIZE = new Set(["send_message", "send_text", "send_email", "send_whatsapp", "send_rcs", "send_sms", "send_push"]);

// A condition node is "grouped" (new Figma shape, 20 Oct) once
// config.conditionGroups exists as an array — anything without it is the old
// flat conditions/booleanCheck shape and keeps rendering/serializing through
// the existing rule/boolean code paths untouched.
export const isGroupedCondition = (config: Record<string, any>): boolean => Array.isArray(config?.conditionGroups);

// Pure, deterministic old-shape -> new-shape converter. Never called
// automatically on load/render (an old journey's condition node keeps its
// `.branches` true/false tree exactly as today) — only invoked the first
// time an operator edits an old-shape condition node through the new groups
// UI, so an untouched old journey is byte-for-byte unaffected by this.
export function migrateConditionConfig(config: Record<string, any>): { groups: ConditionGroup[]; endWorkflowOnFailure: boolean } {
  if (isGroupedCondition(config)) {
    return { groups: config.conditionGroups, endWorkflowOnFailure: !!config.endWorkflowOnFailure };
  }
  if (config.mode === "boolean") {
    return {
      groups: [
        {
          id: `group_${Date.now().toString(36)}`,
          name: config.booleanCheck || "Condition",
          matchLogic: "all",
          rows: [{ id: "row_0", attribute: config.booleanCheck || "", operator: config.booleanTrueOperator || "", value: config.booleanTrueValue ?? "" }],
        },
      ],
      endWorkflowOnFailure: config.falseBehavior === "end",
    };
  }
  return {
    groups: [
      {
        id: `group_${Date.now().toString(36)}`,
        name: config.conditions?.[0]?.attribute || "Condition",
        matchLogic: config.matchLogic || "all",
        rows: (config.conditions || []).map((c: any, i: number) => ({ id: `row_${i}`, attribute: c.attribute, operator: c.operator, value: c.value })),
      },
    ],
    endWorkflowOnFailure: config.falseBehavior === "end",
  };
}

// A Send node's manually-typed body lives under `messageBody` for SMS/Push,
// `description` for everything else — same split config-panel.tsx's
// MessagePreviewEditor and builder-page.tsx's getManualBodyText both use.
const SEND_BODY_FIELD = (type: string) => (type === "send_sms" || type === "send_push" ? "messageBody" : "description");

// Small set of pure, immutable operations on the FlowNode tree — every
// canvas interaction (insert/delete/edit) goes through one of these rather
// than mutating in place, so the builder page can just setState(newRoot).

export function findNode(root: FlowNode | null, id: string): FlowNode | null {
  if (!root) return null;
  if (root.id === id) return root;
  if (root.branches) {
    return findNode(root.branches.true.next, id) ?? findNode(root.branches.false.next, id);
  }
  if (root.optionBranches) {
    for (const b of root.optionBranches) {
      const found = findNode(b.next, id);
      if (found) return found;
    }
    return null;
  }
  return findNode(root.next, id);
}

// POST /v1/bot/flows/compile's own blockers name the offending node by its
// label ("'Welcome' has no channel picked — ...", "'AI Personalise
// variables' has no prompt template. ..."), not by id — this finds every
// node id whose label matches, so a blocker string can be turned into a real
// canvas highlight (same invalidNodeIds mechanism the client-side checks
// already use) instead of only ever showing up as a toast with no visual
// pointer to which card caused it.
export function findNodeIdsByLabel(root: FlowNode | null, label: string): string[] {
  const ids: string[] = [];
  const walk = (node: FlowNode | null) => {
    if (!node) return;
    if (node.label === label) ids.push(node.id);
    if (node.branches) {
      walk(node.branches.true.next);
      walk(node.branches.false.next);
    } else if (node.optionBranches) {
      node.optionBranches.forEach((b) => walk(b.next));
    } else {
      walk(node.next);
    }
  };
  walk(root);
  return ids;
}

export function insertNode(root: FlowNode | null, target: InsertionTarget, newNode: FlowNode): FlowNode {
  if (target.parentId === "trigger") return newNode;

  const walk = (node: FlowNode): FlowNode => {
    if (node.id === target.parentId) {
      if ((target.branch === "true" || target.branch === "false") && node.branches) {
        return {
          ...node,
          branches: {
            ...node.branches,
            [target.branch]: { ...node.branches[target.branch], next: newNode },
          },
        };
      }
      if (target.branch && node.optionBranches) {
        return {
          ...node,
          optionBranches: node.optionBranches.map((b) => (b.optionId === target.branch ? { ...b, next: newNode } : b)),
        };
      }
      return { ...node, next: newNode };
    }
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: node.branches.true.next ? walk(node.branches.true.next) : null },
          false: { ...node.branches.false, next: node.branches.false.next ? walk(node.branches.false.next) : null },
        },
      };
    }
    if (node.optionBranches) {
      return {
        ...node,
        optionBranches: node.optionBranches.map((b) => ({ ...b, next: b.next ? walk(b.next) : null })),
      };
    }
    return { ...node, next: node.next ? walk(node.next) : null };
  };

  return root ? walk(root) : newNode;
}

// Removes one node, splicing its parent straight to whatever came after it
// (so deleting a node in the middle of a chain keeps the rest of the chain
// intact instead of discarding it). A branching node (Condition/Branch) has
// two children rather than one `next` to promote, so deleting one of those
// does take its whole subtree with it — there's no single slot to splice into.
export function deleteNode(root: FlowNode | null, id: string): FlowNode | null {
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    if (node.id === id) return node.branches || node.optionBranches ? null : node.next;
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: walk(node.branches.true.next) },
          false: { ...node.branches.false, next: walk(node.branches.false.next) },
        },
      };
    }
    if (node.optionBranches) {
      return { ...node, optionBranches: node.optionBranches.map((b) => ({ ...b, next: walk(b.next) })) };
    }
    return { ...node, next: walk(node.next) };
  };
  return walk(root);
}

export function updateNodeConfig(root: FlowNode | null, id: string, patch: Record<string, any>): FlowNode | null {
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    if (node.id === id) {
      const config = { ...node.config, ...patch };
      // AI Decide's options list just changed — reconcile optionBranches to
      // match: keep an existing option's subtree (by id), add a fresh open
      // branch for a newly-added option, and drop (with its whole subtree)
      // any branch whose option no longer exists. Without this, removing an
      // option would leave its old path dangling with nothing pointing at it
      // in the UI, yet still very much present in what gets serialized and
      // published — the exact "removed thing still in the payload" bug this
      // session keeps finding elsewhere.
      // Only for a branching Decide — a linear one (no optionBranches, its
      // options just feed the next step) keeps its plain `.next`.
      if (node.type === "ai_decide" && Array.isArray(patch.options) && node.optionBranches) {
        const existing = new Map((node.optionBranches || []).map((b) => [b.optionId, b]));
        const optionBranches = (patch.options as { id: string }[]).map(
          (opt) => existing.get(opt.id) || { optionId: opt.id, behavior: "continue" as const, next: null }
        );
        return { ...node, config, optionBranches };
      }
      // One-time migration trigger (config-panel.tsx fires this the moment an
      // old-shape condition node is opened, not on every edit) — converts a
      // real, already-published journey's flat true/false condition (e.g.
      // Salary-Day Loan's imported "consent_status"/"loan_eligibility"
      // checks) into the new named-group shape, WITHOUT losing what's
      // downstream of it: the real true-branch subtree becomes the migrated
      // group's own branch, and the real false-branch subtree becomes the
      // ON_FAILURE_BRANCH_ID branch — the natural pairing, since "true" is
      // "the condition matched" and "false" is "it didn't".
      if (node.type === "condition" && patch._migrateCondition) {
        const { groups, endWorkflowOnFailure } = migrateConditionConfig(node.config);
        const trueBranch = node.branches?.true;
        const falseBranch = node.branches?.false;
        const optionBranches = [
          { optionId: groups[0].id, behavior: trueBranch?.behavior ?? "continue", next: trueBranch?.next ?? null },
          { optionId: ON_FAILURE_BRANCH_ID, behavior: falseBranch?.behavior ?? (endWorkflowOnFailure ? "end" : "continue"), next: falseBranch?.next ?? null },
        ];
        return {
          ...node,
          config: { ...node.config, conditionGroups: groups, endWorkflowOnFailure: falseBranch ? falseBranch.behavior === "end" : endWorkflowOnFailure },
          optionBranches,
          branches: undefined,
        };
      }
      // Same reconciliation as ai_decide above, for a grouped condition
      // node's groups list (or just its endWorkflowOnFailure toggle)
      // changing — keep a changed/renamed group's existing subtree by id,
      // open a fresh branch for a newly-added group, and drop (with its
      // whole subtree) any branch whose group no longer exists.
      if (node.type === "condition" && (Array.isArray(patch.conditionGroups) || "endWorkflowOnFailure" in patch)) {
        const groups: ConditionGroup[] = Array.isArray(patch.conditionGroups) ? patch.conditionGroups : migrateConditionConfig(node.config).groups;
        const endWorkflowOnFailure = "endWorkflowOnFailure" in patch ? !!patch.endWorkflowOnFailure : !!node.config.endWorkflowOnFailure;
        const existing = new Map((node.optionBranches || []).map((b) => [b.optionId, b]));
        const optionBranches = [
          ...groups.map((g) => existing.get(g.id) || { optionId: g.id, behavior: "continue" as const, next: null }),
          {
            ...(existing.get(ON_FAILURE_BRANCH_ID) || { optionId: ON_FAILURE_BRANCH_ID, next: null }),
            behavior: endWorkflowOnFailure ? ("end" as const) : ("continue" as const),
          },
        ];
        return { ...node, config: { ...config, conditionGroups: groups, endWorkflowOnFailure }, optionBranches };
      }
      return { ...node, config };
    }
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: walk(node.branches.true.next) },
          false: { ...node.branches.false, next: walk(node.branches.false.next) },
        },
      };
    }
    if (node.optionBranches) {
      return { ...node, optionBranches: node.optionBranches.map((b) => ({ ...b, next: walk(b.next) })) };
    }
    return { ...node, next: walk(node.next) };
  };
  return walk(root);
}

// Switches a Send node to a different channel's dedicated catalog type (e.g.
// send_sms -> send_email) — per direct ask: a node imported from a real
// journey (or dropped from the Add panel) is "stuck" on whichever channel it
// started as, with no way to change it short of deleting it and adding a
// fresh one. Picking a template is channel-specific (an SMS template id means
// nothing on an Email node), so that always resets — but a manually-typed
// body carries over into the new channel's own body field rather than being
// silently lost, since the operator's own words aren't tied to any channel.
export function changeSendNodeType(root: FlowNode | null, id: string, newType: string): FlowNode | null {
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    if (node.id === id) {
      const def = findActionDef(newType);
      const carriedBody = node.config?.[SEND_BODY_FIELD(node.type)] || "";
      const config = { ...getDefaultNodeConfig(newType), [SEND_BODY_FIELD(newType)]: carriedBody };
      return { ...node, type: newType, category: def?.category ?? node.category, label: def?.nodeLabel ?? newType, config };
    }
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: walk(node.branches.true.next) },
          false: { ...node.branches.false, next: walk(node.branches.false.next) },
        },
      };
    }
    if (node.optionBranches) {
      return { ...node, optionBranches: node.optionBranches.map((b) => ({ ...b, next: walk(b.next) })) };
    }
    return { ...node, next: walk(node.next) };
  };
  return walk(root);
}

// Condition/Branch — toggling a branch to "end" discards whatever was
// plugged into it; toggling to "continue" just opens up a "+" (no data loss
// the other direction since the subtree is only dropped going into "end").
export function setBranchBehavior(
  root: FlowNode | null,
  id: string,
  branch: "true" | "false",
  behavior: "end" | "continue"
): FlowNode | null {
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    if (node.id === id && node.branches) {
      const current = node.branches[branch];
      return {
        ...node,
        config: { ...node.config, [branch === "true" ? "trueBehavior" : "falseBehavior"]: behavior },
        branches: { ...node.branches, [branch]: { behavior, next: behavior === "end" ? null : current.next } },
      };
    }
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: walk(node.branches.true.next) },
          false: { ...node.branches.false, next: walk(node.branches.false.next) },
        },
      };
    }
    return { ...node, next: walk(node.next) };
  };
  return walk(root);
}

// AI Moderate (and the same-shaped ai_format/ai_summarize) sit between a real
// AI Generate node and the Send node it feeds in every real journey that uses
// one (e.g. Dormancy Reactivation's gen -> mod -> send) — a content check on
// the way out, not an independent source. The previous "preceding node" check
// only ever looked one hop back, so a Send node fed through one of these saw
// "ai_moderate" as its preceding type and never registered as AI-connected at
// all: no "Since AI-Generated node is connected" caption, template picker
// left enabled, and its own "Regenerate" button never actually calling the
// real API. This walks the whole ancestor chain instead, skipping back
// through any number of these content-check steps to find the real source.
const CONTENT_CHECK_TYPES = new Set(["ai_moderate", "ai_format", "ai_summarize"]);

function ancestorChain(root: FlowNode | null, id: string): FlowNode[] {
  const path: FlowNode[] = [];
  const walk = (node: FlowNode | null): boolean => {
    if (!node) return false;
    if (node.id === id) return true;
    path.push(node);
    const children = node.branches
      ? [node.branches.true.next, node.branches.false.next]
      : node.optionBranches
        ? node.optionBranches.map((b) => b.next)
        : [node.next];
    if (children.some((child) => walk(child))) return true;
    path.pop();
    return false;
  };
  walk(root);
  return path;
}

export function findPrecedingAiGenerate(root: FlowNode | null, id: string): FlowNode | undefined {
  const chain = ancestorChain(root, id);
  for (let i = chain.length - 1; i >= 0; i--) {
    if (chain[i].type === "ai_generate") return chain[i];
    if (!CONTENT_CHECK_TYPES.has(chain[i].type)) return undefined;
  }
  return undefined;
}

// Flattens the chain down its default ("true"/first) path for the Test Run
// simulation — a single simulated customer can't meaningfully walk both
// outcomes of a Condition/Branch step at once, so only one side is taken.
// Previously this *stopped* at the first branching node instead of actually
// walking into its default branch — meaning for any journey with a Condition
// before its Send/AI Generate steps (i.e. almost every real journey built
// here, this one included), `steps` never reached them at all: the Test Run
// send call ended up with no real template/content to supply, so the backend
// free-generated unrelated copy using its own demo profile name instead of
// what the operator typed in. Continuing into the "true" (or first named)
// branch — falling back to "false"/the next branch when that one dead-ends —
// fixes that; a node with neither `branches` nor `optionBranches` set despite
// its type just stops, same as before.
export function linearizeForTestRun(root: FlowNode | null): FlowNode[] {
  const out: FlowNode[] = [];
  let node: FlowNode | null = root;
  while (node) {
    out.push(node);
    if (node.optionBranches && node.optionBranches.length > 0) {
      const first = node.optionBranches.find((b) => b.optionId !== ON_FAILURE_BRANCH_ID) || node.optionBranches[0];
      node = first.next;
    } else if (BRANCHING_TYPES.has(node.type) && node.branches) {
      node = node.branches.true?.next || node.branches.false?.next || null;
    } else if (BRANCHING_TYPES.has(node.type)) {
      break;
    } else {
      node = node.next;
    }
  }
  return out;
}

// Maps our own internal per-node UI config into the shape each backend
// node_type actually declares in GET /v1/bot/actions' own `config` template
// — confirmed live against the real test-run gate: send_text (our
// send_whatsapp) wants `template_id`/`message`, not our UI's own
// `templateId`/`description` keys, and the gate's "no template or content"
// blocking reason only clears once one of those real keys is present. Only
// the node types wired to a real selector/content field today are mapped;
// everything else passes its raw config through unchanged.
function serializeNodeConfig(actionId: string, c: Record<string, any>): Record<string, any> {
  switch (actionId) {
    // `channel` is now required in every Send card's serialized config —
    // confirmed: the backend engine dispatches off this field (a generic
    // send node + channel=email compiles to the real email send), not off
    // node type alone, and a picked channel overrides the card's own type.
    // The Channel <SelectField> on these cards only ever called
    // onChangeType (switching which specific card this is) and never wrote
    // this — so the operator's channel pick never reached the engine.
    case "send_whatsapp":
      return { channel: "whatsapp", ...(c.templateId ? { template_id: c.templateId } : {}), ...(c.description ? { message: c.description } : {}) };
    case "send_sms":
      return { channel: "sms", ...(c.messageBody ? { message: c.messageBody } : {}) };
    case "send_email": {
      // GET /v1/bot/actions' send_email config is {to, subject, template_key,
      // html_body, from_name}. This used to send only subject + the plain-
      // text `description` as html_body — a picked template (`template`/
      // `templateLabel`/`templateHtml`) or an Email Editor design
      // (`customHtml`) never reached the engine, so the send layer skipped
      // the card as no_template_or_body (backend, 23 Oct). html_body now
      // follows the same priority as the config panel's own preview: Email
      // Editor design, then the picked template's HTML, then the plain text
      // wrapped in the default design.
      const htmlBody =
        c.useCustomDesign && c.customHtml
          ? c.customHtml
          : c.templateHtml
            ? c.templateHtml
            : c.description
              ? buildDefaultEmailHtml({
                  headerImageUrl: c.headerImageUrl,
                  bodyHtml: c.description,
                  ctaEnabled: c.ctaEnabled,
                  ctaText: c.ctaText,
                  ctaColor: c.ctaColor,
                  footerEnabled: c.footerEnabled,
                  websiteLink: c.websiteLink,
                  instagramLink: c.instagramLink,
                  facebookLink: c.facebookLink,
                  twitterLink: c.twitterLink,
                  footerText: c.footerText,
                })
              : "";
      return {
        channel: "email",
        ...(c.title ? { subject: c.title } : {}),
        // The send layer looks the template up by name (banking_, kyc,
        // bvn_reminder, salary, ...) under template_name/template_key.
        ...(c.templateLabel ? { template_key: c.templateLabel, template_name: c.templateLabel } : {}),
        ...(c.template ? { template_id: c.template } : {}),
        ...(htmlBody ? { html_body: htmlBody } : {}),
      };
    }
    case "condition":
      // New Figma named-group shape (20 Oct) — best-effort serialization, NO
      // confirmed live backend example of a >2-branch condition node yet
      // (postback is only confirmed for ai_decide below). Flag to backend if
      // publishing a grouped condition node 422s.
      if (isGroupedCondition(c)) {
        return {
          groups: (c.conditionGroups as ConditionGroup[]).map((g) => ({ id: g.id, name: g.name, match_logic: g.matchLogic, rows: g.rows })),
          end_workflow_on_failure: !!c.endWorkflowOnFailure,
        };
      }
      // Real shape confirmed against GET /v1/bot/journeys/new_account_welcome's
      // actual stored "KYC completed?" node: {attribute, operator, true_value,
      // false_value} — NOT our UI's own booleanCheck/booleanTrueOperator/etc
      // keys. Without this mapping the backend receives none of the real
      // field names, reads the attribute as unset, and both compile and
      // test-run report "no matching branch and no default" even though the
      // user picked a real attribute/value in the config panel. "rule" mode
      // (If/Else with AND/OR) has no confirmed live example yet — mapped
      // best-effort from its first condition, flag to backend if it 422s.
      return c.mode === "rule"
        ? {
            attribute: c.conditions?.[0]?.attribute || "",
            operator: c.conditions?.[0]?.operator || "is_equal",
            true_value: c.conditions?.[0]?.value ?? "",
          }
        : {
            attribute: c.booleanCheck || "",
            operator: c.booleanTrueOperator || "is_equal",
            true_value: c.booleanTrueValue ?? "",
            false_value: c.booleanFalseValue ?? "",
          };
    case "ai_decide":
      // Real shape confirmed against GET /v1/bot/journeys/churn_risk_escalation's
      // stored "Self-serve vs RM outreach" node: {basis: "lifetime_value"} —
      // a single free-text field, not our UI's decideAttribute/options. The
      // options themselves aren't config at all on the real backend — they're
      // expressed purely as this node's outgoing postback edges (see
      // serializeFlow), so they're deliberately left out here.
      //
      // A linear Decide has no postback edges to carry them, so its options
      // (and the AI instruction) go in config — GET /v1/bot/actions lists
      // ai_decide's config as {basis, options: [string], output_variable}.
      return {
        basis: c.decideAttribute || "",
        ...(c.instruction ? { instruction: c.instruction } : {}),
        ...(Array.isArray(c.options) && c.options.some((o: any) => o.label) ? { options: c.options.filter((o: any) => o.label).map((o: any) => o.label) } : {}),
      };
    case "ai_classify":
      // GET /v1/bot/actions: {input, categories: [string], output_variable}.
      // The tier rules ride along for the engine to use when it can.
      return {
        ...c,
        input: c.classifyAttribute || "",
        ...(Array.isArray(c.tiers) ? { categories: c.tiers.map((t: any) => t.label).filter(Boolean) } : {}),
      };
    default:
      return c;
  }
}

// Flattens the trigger + chain tree into a FlowDefinition for
// POST /v1/bot/test-run (the publish gate). Confirmed against a real 422 from
// the endpoint (Pydantic's own field-required errors): `flow.startNodeId` is
// required, and each edge needs `id`/`source`/`target` — NOT `from`/`to`, and
// not optional.
//
// Node `type` is the backend's own generic engine kind (GET /v1/bot/actions'
// `node_type`, e.g. "send_text", "branch") — NOT our catalog id ("send_whatsapp",
// "condition"). Confirmed live: sending our catalog id as `type` is what
// actually produced "flow has no trigger node" (the trigger node's `type` was
// its specific trigger id, e.g. "salary_credit_detected" — the gate only
// recognizes the literal "trigger"); switching to the real node_type flipped
// the same gate to test_run_passed: true. The trigger's own specific id is
// kept in config (trigger_id) since something has to know which event this
// is, even though the gate itself doesn't need it.
const END_NODE_ID = "end";
const canonicalNodeId = (id: string) => id.replace(/__dup\d+$/, "");

export function serializeFlow(trigger: TriggerInstance, root: FlowNode | null): { startNodeId: string; nodes: any[]; edges: any[] } {
  const nodes: any[] = [{ id: "trigger", type: "trigger", label: trigger.label, config: { ...trigger.config, trigger_id: trigger.id } }];
  const edges: any[] = [];
  const visited = new Set<string>();
  let edgeSeq = 0;
  // A branch set to "end" has no child node in this tree, so it used to emit
  // no edge at all — e.g. loan_abandonment_recovery's "Application
  // completed?" (true -> end) went out with only its false edge, and the
  // engine stopped with "no matching branch" (backend, 23 Oct). Those now
  // point at one shared End node, same as the prebuilt journeys' own `end`
  // (type end_workflow), which chainFromJourneyFlow already treats as
  // terminal when the canvas is reopened.
  let endAdded = false;
  const edgeToEnd = (fromId: string, condition: string, value?: string) => {
    if (!endAdded) {
      nodes.push({ id: END_NODE_ID, type: "end_workflow", label: "End", config: {} });
      endAdded = true;
    }
    edges.push({ id: `edge_${edgeSeq++}`, source: fromId, target: END_NODE_ID, condition, ...(value ? { value } : {}) });
  };

  const walk = (node: FlowNode | null, fromId: string, branch?: "true" | "false" | "parallel", postback?: string, fromSendType?: string) => {
    if (!node) return;
    // Real edge shape confirmed live against GET /v1/bot/journeys/{id} (every
    // prebuilt journey's own edges use `condition: "always"/"true"/"false"`)
    // AND against POST /v1/bot/flows/compile directly: an edge tagged
    // `branch: "true"/"false"` (this function's old key) is silently ignored
    // by the compiler — ALL of a branch's downstream steps get flattened into
    // one always-executing sequence with no blocker raised, while the same
    // edges tagged `condition: "true"/"false"` are correctly recognized as a
    // real branch (confirmed via a live compile call that only then produced
    // the expected "no default path" blocker). This means every Condition/
    // Branch node compiled or published before this fix had its branching
    // silently discarded by the engine.
    //
    // AI Decide's own named options are a third shape — confirmed live against
    // GET /v1/bot/journeys/churn_risk_escalation: `condition: "postback"` with
    // a `value` naming which option (e.g. "self_serve"/"high_value"), not
    // "true"/"false".
    //
    // A Send node's own `.branches` (pending backend deploy, per direct ask)
    // are a fourth shape: its real edges are `condition: "always"` (the
    // delivered path) and `condition: "fallback"` (taken when delivery
    // fails), not "true"/"false" — re-serializing an edited one as plain
    // true/false would silently lose that distinction on the next
    // compile/publish/test-run.
    const sendCondition = fromSendType && branch ? (branch === "true" ? "always" : "fallback") : undefined;
    // A node reached from two parents (branches reconverging) is imported as
    // `<id>__dup<n>` copies — see journey-flow-import.ts's buildNode. Each
    // copy maps back to its original id here, so the engine gets the one
    // shared node with an edge from every parent, not orphaned duplicates
    // (backend found engaged__dup1..3 with no edges at all, 23 Oct).
    const nodeId = canonicalNodeId(node.id);
    const edge: any = postback
      ? { id: `edge_${edgeSeq++}`, source: fromId, target: nodeId, condition: "postback", value: postback }
      : { id: `edge_${edgeSeq++}`, source: fromId, target: nodeId, condition: sendCondition || branch || "always" };
    edges.push(edge);
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    nodes.push({ id: nodeId, type: findActionDef(node.type)?.nodeType || node.type, label: node.label, config: serializeNodeConfig(node.type, node.config) });
    if (node.branches) {
      const sendType = SEND_TYPES_FOR_SERIALIZE.has(node.type) ? node.type : undefined;
      // A Branch fans out — both paths run — and its edges are
      // `condition: "parallel"`, as in GET /v1/bot/journeys/fraud_anomaly_alert
      // (fan -> sms, fan -> push). Sent as true/false, the engine reads it as
      // a condition with nothing to check and only one alert goes out.
      const parallel = node.type === "branch";
      (["true", "false"] as const).forEach((side) => {
        const b = node.branches![side];
        const edgeSide = parallel ? "parallel" : side;
        if (b.next) walk(b.next, nodeId, edgeSide, undefined, sendType);
        else if (b.behavior === "end") edgeToEnd(nodeId, sendType ? (side === "true" ? "always" : "fallback") : edgeSide);
      });
    } else if (node.optionBranches) {
      node.optionBranches.forEach((b) => {
        if (b.next) walk(b.next, nodeId, undefined, b.optionId);
        else if (b.behavior === "end") edgeToEnd(nodeId, "postback", b.optionId);
      });
    } else if (node.type === "ai_moderate" && node.next) {
      // Never a convergent pair for a moderation gate — the compiler hoists
      // a shared target past the condition, so the Send would run on a
      // refusal too. Passed continues; refused ends.
      walk(node.next, nodeId, "true");
      edgeToEnd(nodeId, "false");
    } else if (BRANCHING_TYPES.has(node.type) && node.next) {
      // A condition whose true and false both lead to the same step is
      // imported as a plain `.next` (journey-flow-import.ts) — it still has
      // to go out as a true + false pair, or the engine sees a condition
      // with neither branch.
      walk(node.next, nodeId, "true");
      walk(node.next, nodeId, "false");
    } else {
      walk(node.next, nodeId);
    }
  };
  walk(root, "trigger");
  return { startNodeId: "trigger", nodes, edges };
}

// Wait — the only plain-linear node with an explicit "THEN" end/continue
// toggle; switching to "end" drops whatever was wired after it.
export function setWaitThenBehavior(root: FlowNode | null, id: string, behavior: "end" | "continue"): FlowNode | null {
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    if (node.id === id) {
      return { ...node, config: { ...node.config, thenBehavior: behavior }, next: behavior === "end" ? null : node.next };
    }
    if (node.branches) {
      return {
        ...node,
        branches: {
          true: { ...node.branches.true, next: walk(node.branches.true.next) },
          false: { ...node.branches.false, next: walk(node.branches.false.next) },
        },
      };
    }
    if (node.optionBranches) {
      return { ...node, optionBranches: node.optionBranches.map((b) => ({ ...b, next: walk(b.next) })) };
    }
    return { ...node, next: walk(node.next) };
  };
  return walk(root);
}
